import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChatsCircle } from '@phosphor-icons/react';

import Header from '../components/header';
import { HeaderContent, HEADER_TITLE_CLASS, HEADER_BADGE_CLASS } from '../components/shell/app-header';
import { ConversationDetailView } from '../components/admin/conversation-view';
import { Button } from '@/components/ui/button';
import { useNavigate, useParams } from '../lib/routing';
import { useLocalStorage } from '../hooks/use-local-storage';
import { useIsAdminViewer } from '../hooks/use-is-admin-viewer';
import { deleteThread } from '../lib/admin';
import { fetchConversation, type AdminConversationDetail } from '../lib/admin-conversations';
import type { ConversationReviewSurface } from '../components/admin/conversation-comments';
import {
  addComment,
  editComment,
  fetchComments,
  removeComment,
  type ConversationComment,
} from '../lib/admin-conversation-comments';
import { useAuth } from '../hooks/use-auth';

const RENDER_KEY = 'naia:admin:conversations:rendered';

const HEADER_ICON = <ChatsCircle size={26} />;

export default function AdminConversationDetail() {
  const isAdminViewer = useIsAdminViewer();
  const params = useParams<{ id?: string }>();
  const id = params.id ?? '';
  const navigate = useNavigate();

  const [detail, setDetail] = useState<AdminConversationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [generation, setGeneration] = useState(0);
  const [rendered, setRendered] = useLocalStorage<boolean>(RENDER_KEY, false);
  const expanded = useRef(false);

  const { user } = useAuth();
  const [comments, setComments] = useState<readonly ConversationComment[]>([]);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  /* Les commentaires sont chargés à part du fil : un échec côté revue ne doit
     pas empêcher de LIRE la conversation, qui est la raison d'être de l'écran.
     Un droit de relecture refusé se lit ici comme une erreur de revue, pas
     comme une conversation introuvable. */
  useEffect(() => {
    if (!id) return;
    let alive = true;
    fetchComments(id)
      .then((rows) => {
        if (alive) setComments(rows);
      })
      .catch((e) => {
        if (alive) setReviewError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      alive = false;
    };
  }, [id]);

  /* Après chaque écriture on RELIT la liste plutôt que d'y recoller la ligne
     rendue : deux relecteurs peuvent commenter le même fil en même temps, et un
     état reconstruit localement leur cacherait le commentaire de l'autre. */
  const runReview = useCallback(
    async (op: () => Promise<unknown>): Promise<boolean> => {
      if (!id) return false;
      setReviewBusy(true);
      setReviewError(null);
      try {
        await op();
        setComments(await fetchComments(id));
        return true;
      } catch (e) {
        setReviewError(e instanceof Error ? e.message : String(e));
        return false;
      } finally {
        setReviewBusy(false);
      }
    },
    [id],
  );

  const review = useMemo<ConversationReviewSurface>(
    () => ({
      comments,
      busy: reviewBusy,
      error: reviewError,
      currentUserId: user?.id ?? null,
      add: (input) => runReview(() => addComment({ threadId: id, ...input })),
      edit: (commentId, patch) => runReview(() => editComment(commentId, patch)),
      remove: (commentId) => runReview(() => removeComment(commentId)),
    }),
    [comments, reviewBusy, reviewError, user?.id, runReview, id],
  );

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    fetchConversation(id)
      .then(setDetail)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [id]);

  const title = useMemo(() => {
    const row = detail?.conversation;
    return row?.title?.trim() || row?.preview?.trim() || 'Conversation';
  }, [detail]);

  const toggleAll = () => {
    expanded.current = !expanded.current;
    setGeneration((g) => (expanded.current ? g + 1 : 0));
  };

  const handleDelete = () => {
    if (!id) return;
    if (!window.confirm(`Supprimer la conversation « ${title} » et tous ses messages ?`)) return;
    setDeleting(true);
    setError(null);
    deleteThread(id)
      .then(() => navigate('/admin/conversations'))
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setDeleting(false);
      });
  };

  return (
    <>
      <Header chromeless />
      <HeaderContent
        width="xl"
        showBurger
        backTo="/admin/conversations"
        backLabel="Conversations"
        icon={HEADER_ICON}
        actions={
          detail ? (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setRendered(!rendered)} title="Le texte brut est la valeur stockée ; le rendu Markdown n'est qu'une aide à la lecture.">
                {rendered ? 'Texte brut' : 'Rendu Markdown'}
              </Button>
              <Button variant="outline" size="sm" onClick={toggleAll}>
                {generation > 0 ? 'Tout replier' : 'Tout déplier'}
              </Button>
              {isAdminViewer && (
                <Button variant="outline" size="sm" onClick={handleDelete} disabled={deleting}>
                  {deleting ? 'Suppression…' : 'Supprimer'}
                </Button>
              )}
            </div>
          ) : undefined
        }
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <h1 className={HEADER_TITLE_CLASS}>{title}</h1>
          <span className={HEADER_BADGE_CLASS}>Admin</span>
        </div>
      </HeaderContent>

      <main className="mx-auto max-w-[1400px] px-4 pt-4 pb-16">
        {loading && <p className="text-[13px] text-[var(--naia-text-secondary)]">Chargement…</p>}

        {error && (
          <div className="rounded-[var(--naia-r-card)] border border-[var(--naia-line)] bg-[var(--naia-error-bg)] px-4 py-3 text-[13px] text-[var(--naia-error)]">
            {error}
          </div>
        )}

        {!loading && !error && !detail && (
          <p className="text-[13px] text-[var(--naia-text-secondary)]">Conversation introuvable.</p>
        )}

        {detail && (
          <ConversationDetailView detail={detail} generation={generation} rendered={rendered} review={review} />
        )}
      </main>
    </>
  );
}
